// Form-driven document templates.
// Each template defines:
//   • sections: [{ id, title, fields: [...] }]
//   • advancedSections: [{ id, title, fields: [...] }]   // boilerplate, hidden until expanded
//   • render(values, ctx): -> HTML string (uses the values + ctx.shipment + ctx.buyer)
// Field shape: { key, label, type, default, span?, placeholder? }
//   span: 1 (default, half on desktop) | 2 (full row) | "full"

import { documentHeader, bigTitleHeader, logoOnlyHeader, markOnlyHeader, documentFooter } from "./docLogo";

const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};
const safe = (v, dash = "—") => (v == null || v === "" ? dash : String(v));
const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]));
// Replace newlines with <br> for textarea fields
const ml = (v) => esc(v).replace(/\n/g, "<br>");

const COMPANY = {
  name: "TradeRepubliq LLC-FZ",
  addr: "Meydan Grandstand, 6th floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E.",
  email: "support@traderepubliq.com",
  phone: "+971 58 112 0336",
  contact: "Abdullahi Kassim",
  trade_license: "2420040.01",
};

// ── Shared helpers ──────────────────────────────────────────────────────────
const sectionTitle = (n) => `<h2 style="color:#6B0F1A;font-size:14px;border-bottom:2px solid #6B0F1A;padding-bottom:4px;margin-top:14px;">${n}</h2>`;

// =============================================================================
// 1. Service Level Agreement
// =============================================================================
const SLA = {
  id: "sla",
  title: "Service Level Agreement",
  subtitle: "Governing framework for trade transactions",
  defaults: (ctx) => ({
    sla_reference: ctx.shipment?.sla_reference || "",
    seller_name: COMPANY.name,
    seller_zone: "Meydan Free Zone, Dubai, UAE",
    seller_tlc: COMPANY.trade_license,
    seller_addr: COMPANY.addr,
    buyer_name: ctx.buyer?.company_name || "",
    buyer_country: ctx.buyer?.country || "",
    buyer_regno: ctx.buyer?.registration_no || "",
    buyer_addr: ctx.buyer?.address || "",
    sla_date: today(),
    incoterm: "CIF (Incoterms® 2020)",
    currency: "USD",
    pay1: "40", pay2: "30", pay3: "30",
    pay3_days: "7",
    cutoff_days: "21",
    validity: "one year from the date of signing",
    advance_purpose: "This Agreement establishes the governing commercial, operational, and legal framework for all transactions between the parties. All transactions shall be executed through:\n• A sales confirmation (SC) issued by the Seller and accepted by the Buyer\n• A complete shipment documentation file (\"Transaction File\")\nThe sales confirmation serves as the primary and binding document for each transaction.",
    advance_quality: "9.1 Pre-shipment control: Inspection conducted at origin; PSQIR issued; proper packing, pre-cooling, and loading ensured.\n9.2 Cold chain integrity: Container condition verified before loading; reefer settings recorded at stuffing.\n9.3 Quality disputes: Any claim must demonstrate a deviation from PSQIR and that the issue existed before shipment. All inspections at destination must be conducted through joint inspection by a mutually agreed independent surveyor.",
    advance_claims: "10.1 Reserve notice: Buyer shall provide written notice of any loss/damage at the time of discharge. For non-apparent loss, within 48 hours from discharge.\n10.2 Full claim: Within 7 days, including: joint survey report, temperature logs, photos, salvage documentation, destruction certificate from authorized agency.\n10.3 Failure to comply with timelines or documentation requirements renders the claim invalid.\n10.6 Burden of proof: Buyer must demonstrate that goods deviated from agreed standards and that the issue existed prior to shipment.",
    advance_disputes: "Governing law: UAE · Jurisdiction: Dubai Courts or agreed arbitration.",
  }),
  sections: [
    { id: "header", title: "Header", fields: [
      { key: "sla_reference", label: "SLA Reference", type: "text", span: 2 },
      { key: "sla_date", label: "Date", type: "date", span: 1 },
      { key: "validity", label: "Validity", type: "text", span: 1 },
    ]},
    { id: "seller", title: "Seller (Party 1)", fields: [
      { key: "seller_name", label: "Legal Name", type: "text", span: 2 },
      { key: "seller_zone", label: "Free Zone", type: "text", span: 1 },
      { key: "seller_tlc", label: "Trade License No.", type: "text", span: 1 },
      { key: "seller_addr", label: "Registered Address", type: "textarea", span: 2 },
    ]},
    { id: "buyer", title: "Buyer (Party 2)", fields: [
      { key: "buyer_name", label: "Legal Name", type: "text", span: 2 },
      { key: "buyer_country", label: "Country of Incorporation", type: "text", span: 1 },
      { key: "buyer_regno", label: "Registration No.", type: "text", span: 1 },
      { key: "buyer_addr", label: "Registered Address", type: "textarea", span: 2 },
    ]},
    { id: "commercial", title: "Commercial Framework", fields: [
      { key: "incoterm", label: "Default Incoterm", type: "text", span: 1 },
      { key: "currency", label: "Default Currency", type: "text", span: 1 },
    ]},
    { id: "payment", title: "Payment Structure", fields: [
      { key: "pay1", label: "Order Confirmation %", type: "number", span: 1 },
      { key: "pay2", label: "Against Documents %", type: "number", span: 1 },
      { key: "pay3", label: "Post Arrival %", type: "number", span: 1 },
      { key: "pay3_days", label: "Post Arrival Days", type: "number", span: 1 },
      { key: "cutoff_days", label: "Order Cut-off (days before sailing)", type: "number", span: 2 },
    ]},
  ],
  advancedSections: [
    { id: "purpose", title: "Purpose & Structure (Sec 2)", fields: [
      { key: "advance_purpose", label: "Purpose paragraph", type: "textarea", span: "full", rows: 6 },
    ]},
    { id: "quality", title: "Quality & Inspection (Sec 9)", fields: [
      { key: "advance_quality", label: "Quality clauses", type: "textarea", span: "full", rows: 6 },
    ]},
    { id: "claims", title: "Claims & Liability (Sec 10)", fields: [
      { key: "advance_claims", label: "Claims framework", type: "textarea", span: "full", rows: 7 },
    ]},
    { id: "disputes", title: "Dispute Resolution (Sec 13)", fields: [
      { key: "advance_disputes", label: "Disputes & jurisdiction", type: "textarea", span: "full", rows: 3 },
    ]},
  ],
  render(v, ctx) {
    return `
    <h1 style="text-align:center;color:#6B0F1A;font-size:22px;margin:0 0 4px;">Service Level Agreement (SLA)</h1>
    ${v.sla_reference ? `<p style="text-align:center;color:#1F1F1F;font-size:11px;margin:0 0 4px;font-weight:bold;letter-spacing:0.4px;">Ref: ${esc(v.sla_reference)}</p>` : ""}
    <p style="text-align:center;color:#888;font-size:11px;margin:0 0 16px;">Date: ${fmtDate(v.sla_date)}</p>

    ${sectionTitle("1. Parties")}
    <p><strong>Seller</strong><br>${esc(v.seller_name)}<br>${esc(v.seller_zone)}<br>Trade License No.: ${esc(v.seller_tlc)}<br>Registered Address: ${esc(v.seller_addr)}<br>(hereinafter "the Seller")</p>
    <p><strong>Buyer</strong><br>${esc(v.buyer_name)}<br>Incorporated under the laws of ${esc(v.buyer_country)}<br>Registration No.: ${esc(v.buyer_regno)}<br>Registered Address: ${esc(v.buyer_addr)}<br>(hereinafter "the Buyer")</p>

    ${sectionTitle("2. Purpose")}
    <p>${ml(v.advance_purpose)}</p>

    ${sectionTitle("3. Agreement Structure")}
    <p>Order of precedence: (1) This Agreement, (2) Sales Confirmation (SC), (3) Buyer Purchase Order subject to (1) and (2).</p>

    ${sectionTitle("4. Commercial Framework")}
    <p><strong>Incoterm:</strong> ${esc(v.incoterm)}<br>
    <strong>Pricing:</strong> Defined per shipment in the SC. Once accepted, final and binding.<br>
    <strong>Currency:</strong> ${esc(v.currency)} unless otherwise agreed.</p>

    ${sectionTitle("5. Payment Terms and Title")}
    <ul>
      <li><strong>1st — Order Confirmation:</strong> ${esc(v.pay1)}% upon confirmation</li>
      <li><strong>2nd — Against Documents:</strong> ${esc(v.pay2)}% against documents</li>
      <li><strong>Balance — Post Arrival:</strong> ${esc(v.pay3)}% within ${esc(v.pay3_days)} days after arrival</li>
    </ul>
    <p>Title remains with the Seller until full payment is received.</p>

    ${sectionTitle("6. Order and Execution Discipline")}
    <p>Order cut-off: <strong>${esc(v.cutoff_days)} days</strong> prior to vessel sailing. Preparation timelines shall not be shortened.</p>

    ${sectionTitle("7. Transaction Confirmation")}
    <p>Each shipment is confirmed via a Sales Confirmation containing buyer details, product specs, quantity, container type, price, payment terms, Incoterms, shipment window, and any special conditions. Acceptance must be confirmed in writing via official email.</p>

    ${sectionTitle("8. Shipment Documentation File")}
    <p>Each shipment includes: Commercial Invoice · Packing List · Phytosanitary Certificate · Certificate of Origin · Bill of Lading · PSQIR · other export documents.</p>

    ${sectionTitle("9. Quality, Inspection and Cold Chain")}
    <p>${ml(v.advance_quality)}</p>

    ${sectionTitle("10. Claims and Liability Framework")}
    <p>${ml(v.advance_claims)}</p>

    ${sectionTitle("11. Rights and Obligations")}
    <p><strong>Buyer:</strong> Raise claims, suspend in case of material breach, terminate for repeated failures.<br>
    <strong>Seller:</strong> Suspend for payment default or breach, terminate for repeated non-compliance.</p>

    ${sectionTitle("12. Force Majeure")}
    <p>Neither party shall be liable for events beyond reasonable control: port congestion, vessel delays, government actions, carrier disruptions.</p>

    ${sectionTitle("13. Dispute Resolution")}
    <p>${ml(v.advance_disputes)}</p>

    ${sectionTitle("14. Validity")}
    <p>This Agreement remains valid for <strong>${esc(v.validity)}</strong>. All confirmed transactions remain enforceable.</p>

    ${sectionTitle("16. Acceptance")}
    <table style="width:100%;margin-top:18px;"><tr>
      <td style="padding:8px;width:50%;"><strong>Seller</strong><br>${esc(v.seller_name)}<br><br>Name: ____________________<br><br>Signature: ____________________</td>
      <td style="padding:8px;width:50%;"><strong>Buyer</strong><br>${esc(v.buyer_name)}<br><br>Name: ____________________<br><br>Signature: ____________________</td>
    </tr></table>`;
  },
};

// =============================================================================
// 2. Sales Confirmation
// =============================================================================
const SC = {
  id: "sales_confirmation",
  title: "Sales Confirmation",
  subtitle: "Per-shipment binding sale confirmation",
  defaults: (ctx) => ({
    sc_no: ctx.shipment?.reference || "",
    sc_date: ctx.shipment?.created_at ? ctx.shipment.created_at.slice(0, 10) : today(),
    sla_ref: "",
    buyer_name: ctx.buyer?.company_name || "",
    buyer_addr: ctx.buyer?.address || "",
    buyer_country: ctx.buyer?.country || "",
    product: ctx.shipment?.product || "",
    variety: ctx.shipment?.variety || "",
    grade: "",
    packaging: "",
    total_qty: "",
    num_containers: "",
    unit_price: "",
    total_price: "",
    incoterm: "CIF (Incoterms® 2020)",
    port_loading: "",
    port_discharge: "",
    vessel: "",
    etd: "",
    eta: "",
    pay1: "40", pay2: "30", pay3: "30", pay3_days: "7",
    advance_insurance: "Insurance to be arranged by seller.",
    advance_docs: "Commercial Invoice · Packing List · Phytosanitary Certificate · Certificate of Origin · Bill of Lading · PSQIR.",
    advance_claims: "Reserve notice within 24 hours. Full claim within 7 days. (As per Maersk BL.)",
  }),
  sections: [
    { id: "header", title: "Header", fields: [
      { key: "sc_no", label: "Sales Confirmation No.", type: "text", span: 1 },
      { key: "sc_date", label: "Date", type: "date", span: 1 },
      { key: "sla_ref", label: "SLA Reference", type: "text", span: 2 },
    ]},
    { id: "parties", title: "Buyer", fields: [
      { key: "buyer_name", label: "Buyer Company", type: "text", span: 2 },
      { key: "buyer_addr", label: "Address", type: "text", span: 1 },
      { key: "buyer_country", label: "Country", type: "text", span: 1 },
    ]},
    { id: "product", title: "Product Details", fields: [
      { key: "product", label: "Product", type: "text", span: 1 },
      { key: "variety", label: "Variety", type: "text", span: 1 },
      { key: "grade", label: "Grade / Specification", type: "text", span: 1 },
      { key: "packaging", label: "Packaging", type: "text", span: 1 },
    ]},
    { id: "qty", title: "Quantity & Price", fields: [
      { key: "total_qty", label: "Total Quantity", type: "text", span: 1 },
      { key: "num_containers", label: "Number of Containers", type: "text", span: 1 },
      { key: "unit_price", label: "Unit Price (USD)", type: "text", span: 1 },
      { key: "total_price", label: "Total Contract Value (USD)", type: "text", span: 1 },
    ]},
    { id: "delivery", title: "Delivery & Shipment", fields: [
      { key: "incoterm", label: "Incoterm", type: "text", span: 2 },
      { key: "port_loading", label: "Port of Loading", type: "text", span: 1 },
      { key: "port_discharge", label: "Port of Discharge", type: "text", span: 1 },
      { key: "vessel", label: "Vessel / Voyage", type: "text", span: 2 },
      { key: "etd", label: "ETD", type: "date", span: 1 },
      { key: "eta", label: "ETA", type: "date", span: 1 },
    ]},
    { id: "payment", title: "Payment", fields: [
      { key: "pay1", label: "Advance %", type: "number", span: 1 },
      { key: "pay2", label: "Against Docs %", type: "number", span: 1 },
      { key: "pay3", label: "Post Arrival %", type: "number", span: 1 },
      { key: "pay3_days", label: "Post Arrival Days", type: "number", span: 1 },
    ]},
  ],
  advancedSections: [
    { id: "ins", title: "Insurance", fields: [{ key: "advance_insurance", label: "Insurance clause", type: "textarea", span: "full", rows: 2 }]},
    { id: "doc", title: "Documentation", fields: [{ key: "advance_docs", label: "Documentation list", type: "textarea", span: "full", rows: 2 }]},
    { id: "cl", title: "Claims & Liability", fields: [{ key: "advance_claims", label: "Claims clause", type: "textarea", span: "full", rows: 3 }]},
  ],
  render(v) {
    return `
    ${logoOnlyHeader("SALES CONFIRMATION")}
    <table style="width:100%;margin-bottom:12px;"><tr>
      <td><strong>Sales Confirmation No:</strong> ${esc(v.sc_no)}</td>
      <td style="text-align:right;"><strong>Date:</strong> ${fmtDate(v.sc_date)}</td>
    </tr></table>
    ${sectionTitle("1. Parties")}
    <p><strong>Seller:</strong> ${COMPANY.name}<br>
    <strong>Buyer:</strong> ${esc(v.buyer_name)}, ${esc(v.buyer_addr)}, ${esc(v.buyer_country)}</p>
    ${sectionTitle("2. Reference")}<p>SLA Reference: ${esc(v.sla_ref) || "____________________"}</p>
    ${sectionTitle("3. Product Details")}
    <table border="1" cellpadding="6" style="width:100%;border-collapse:collapse;">
      <tr><td><strong>Product</strong></td><td>${esc(v.product)}</td></tr>
      <tr><td><strong>Variety</strong></td><td>${esc(v.variety)}</td></tr>
      <tr><td><strong>Grade / Specification</strong></td><td>${esc(v.grade)}</td></tr>
      <tr><td><strong>Packaging</strong></td><td>${esc(v.packaging)}</td></tr>
    </table>
    ${sectionTitle("4. Quantity")}
    <p>Total Quantity: ${esc(v.total_qty)}<br>Number of Containers: ${esc(v.num_containers)}</p>
    ${sectionTitle("5. Price")}
    <p>Price per Unit: USD ${esc(v.unit_price)}<br>Total Contract Value: USD ${esc(v.total_price)}</p>
    ${sectionTitle("6. Delivery Terms")}
    <p>Incoterm: <strong>${esc(v.incoterm)}</strong><br>Port of Loading: ${esc(v.port_loading)}<br>Port of Discharge: ${esc(v.port_discharge)}</p>
    ${sectionTitle("7. Shipment Details")}
    <p>Vessel / Voyage: ${esc(v.vessel)}<br>ETD: ${fmtDate(v.etd)}<br>ETA: ${fmtDate(v.eta)}</p>
    ${sectionTitle("8. Payment Terms")}
    <ul>
      <li>Advance: ${esc(v.pay1)}% upon confirmation</li>
      <li>Against documents: ${esc(v.pay2)}%</li>
      <li>Balance: ${esc(v.pay3)}% within ${esc(v.pay3_days)} days after arrival</li>
    </ul>
    ${sectionTitle("9. Insurance")}<p>${ml(v.advance_insurance)}</p>
    ${sectionTitle("10. Documentation")}<p>${ml(v.advance_docs)}</p>
    ${sectionTitle("11. Claims & Liability")}<p>${ml(v.advance_claims)}</p>
    ${sectionTitle("12. Finality")}<p>This document represents the complete and final agreement for this transaction.</p>
    ${sectionTitle("13. Signatures")}
    <table style="width:100%;margin-top:12px;"><tr>
      <td style="padding:6px;"><strong>Seller (CEO):</strong> ____________________</td>
      <td style="padding:6px;"><strong>Buyer:</strong> ____________________</td>
    </tr></table>
    ${documentFooter()}`;
  },
};

// =============================================================================
// 3. Commercial Invoice
// =============================================================================
const CI = {
  id: "commercial_invoice",
  title: "Commercial Invoice",
  subtitle: "Customs and payment invoice",
  defaults: (ctx) => ({
    invoice_no: ctx.shipment?.reference || "",
    invoice_date: today(),
    export_license: "",
    container_no: "",
    method: "Sea",
    shipment_type: "Palletized Cargo",
    country_origin: "",
    country_dest: ctx.buyer?.country || "",
    vessel: "",
    voyage: "",
    port_loading: "",
    port_discharge: "",
    place_of_receipt: "",
    marine_policy: "N/A",
    letter_of_credit: "N/A",
    etd: "",
    eta: "",
    incoterm: "CIF (Sea Freight)",
    incoterm_year: String(new Date().getFullYear()),
    currency: "USD",
    payment_terms: "70% upon receipt of scanned documents and 30% after cargo arrival.",
    shipper_name: "", shipper_addr: "", shipper_email: "", shipper_phone: "",
    consignee_name: ctx.buyer?.company_name || "",
    consignee_addr: ctx.buyer?.address || "",
    consignee_country: ctx.buyer?.country || "",
    consignee_email: ctx.buyer?.email || "",
    consignee_phone: ctx.buyer?.phone || "",
    line_desc: ctx.shipment?.product
      ? `${ctx.shipment.product}${ctx.shipment.variety ? ` — ${ctx.shipment.variety}` : ""}`
      : "",
    net_weight: "",
    gross_weight: "",
    unit_qty: "",
    price_per_unit: "",
    line_amount: "",
    additional_info: "The Exporter of the Products covered by this document declares that, except where otherwise clearly indicated, these Products are of preferential origin.",
    description_text: `This shipment consists of boxes of ${ctx.shipment?.product || "____"} (specifications as per buyer requirements), transported by sea under refrigerated conditions as per the shipper's instructions. The shipment is classified under Shipper's Load, Stow, Weight, and Count, ensuring proper handling and temperature control during transit.`,
    bank_name: "Emirates Islamic",
    branch_name: "El Qasimiyah",
    account_no: "3708503921902",
    bic: "MEBLAEADXXX",
    iban: "AE490340003708503921902",
    bank_address: "Al Otaiba Building, King Abdul Aziz St – Al Nad - Hay Al Qasimiah - Sharjah",
    signatory_company: COMPANY.name,
    signatory: COMPANY.contact,
  }),
  sections: [
    { id: "header", title: "Invoice Header", fields: [
      { key: "invoice_no", label: "Commercial Invoice Number", type: "text", span: 1 },
      { key: "invoice_date", label: "Commercial Invoice Date", type: "date", span: 1 },
      { key: "export_license", label: "Export License No.", type: "text", span: 1 },
      { key: "container_no", label: "Container No.", type: "text", span: 1 },
    ]},
    { id: "shipper", title: "Shipper", fields: [
      { key: "shipper_name", label: "Shipper Name", type: "text", span: 2 },
      { key: "shipper_addr", label: "Address", type: "textarea", span: 2, rows: 2 },
      { key: "shipper_email", label: "Email", type: "text", span: 1 },
      { key: "shipper_phone", label: "Phone", type: "text", span: 1 },
    ]},
    { id: "consignee", title: "Consignee (Buyer)", fields: [
      { key: "consignee_name", label: "Name", type: "text", span: 2 },
      { key: "consignee_addr", label: "Address", type: "text", span: 1 },
      { key: "consignee_country", label: "Country", type: "text", span: 1 },
      { key: "consignee_email", label: "Email", type: "text", span: 1 },
      { key: "consignee_phone", label: "Phone", type: "text", span: 1 },
    ]},
    { id: "ship", title: "Shipment", fields: [
      { key: "method", label: "Method of Dispatch", type: "text", span: 1 },
      { key: "shipment_type", label: "Type of Shipment", type: "text", span: 1 },
      { key: "country_origin", label: "Country of origin of goods", type: "text", span: 1 },
      { key: "country_dest", label: "Country of final destination", type: "text", span: 1 },
      { key: "vessel", label: "Vessel / Aircraft", type: "text", span: 1 },
      { key: "voyage", label: "Voyage Number", type: "text", span: 1 },
      { key: "port_loading", label: "Port of Loading", type: "text", span: 1 },
      { key: "port_discharge", label: "Port of Discharge", type: "text", span: 1 },
      { key: "place_of_receipt", label: "Place of Receipt", type: "text", span: 1 },
      { key: "etd", label: "ETD", type: "date", span: 1 },
      { key: "eta", label: "ETA", type: "date", span: 1 },
      { key: "marine_policy", label: "Marine Cover Policy", type: "text", span: 1 },
      { key: "letter_of_credit", label: "Letter Of Credit No", type: "text", span: 1 },
    ]},
    { id: "terms", title: "Terms & Currency", fields: [
      { key: "incoterm", label: "Incoterms", type: "text", span: 1 },
      { key: "incoterm_year", label: "Incoterm Year", type: "text", span: 1 },
      { key: "currency", label: "Currency", type: "text", span: 1 },
      { key: "payment_terms", label: "Method of Payment", type: "textarea", span: 2, rows: 2 },
    ]},
    { id: "line", title: "Line Item", fields: [
      { key: "line_desc", label: "Description of goods", type: "text", span: 2 },
      { key: "net_weight", label: "Net Weight", type: "text", span: 1 },
      { key: "gross_weight", label: "Gross Weight", type: "text", span: 1 },
      { key: "unit_qty", label: "Unit Quantity (boxes)", type: "text", span: 1 },
      { key: "price_per_unit", label: "Price per Unit", type: "text", span: 1 },
      { key: "line_amount", label: "Amount", type: "text", span: 2 },
    ]},
    { id: "bank", title: "Payment Information", fields: [
      { key: "bank_name", label: "Bank Name", type: "text", span: 1 },
      { key: "branch_name", label: "Branch Name", type: "text", span: 1 },
      { key: "account_no", label: "Account Number", type: "text", span: 1 },
      { key: "bic", label: "BIC Code", type: "text", span: 1 },
      { key: "iban", label: "IBAN", type: "text", span: 2 },
      { key: "bank_address", label: "Bank Address", type: "textarea", span: 2, rows: 2 },
      { key: "signatory_company", label: "Signatory Company", type: "text", span: 1 },
      { key: "signatory", label: "Authorised Signatory", type: "text", span: 1 },
    ]},
  ],
  advancedSections: [
    { id: "desc", title: "Description of Goods (paragraph)", fields: [{ key: "description_text", label: "Description paragraph", type: "textarea", span: "full", rows: 4 }]},
    { id: "addl", title: "Additional Info", fields: [{ key: "additional_info", label: "Additional info / origin declaration", type: "textarea", span: "full", rows: 3 }]},
  ],
  render(v) {
    // Compact, single-page-friendly Commercial Invoice that matches the reference
    // PDF: logo-only header, burgundy section labels with black values, and a
    // very subtle burgundy background tint across the page.
    const cellLabel = "padding:3px 5px;font-size:8.5px;color:#6B0F1A;font-weight:bold;text-transform:uppercase;letter-spacing:0.3px;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;";
    const cellValue = "padding:3px 5px;font-size:10px;color:#1F1F1F;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;line-height:1.3;";
    const cellLabelLast = cellLabel.replace("border-right:1px solid #D4B5B8;", "");
    const cellValueLast = cellValue.replace("border-right:1px solid #D4B5B8;", "");
    const partyHead = "background:#F4E8E9;padding:4px 7px;font-size:8.5px;color:#6B0F1A;font-weight:bold;text-transform:uppercase;letter-spacing:0.3px;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;";
    const partyCell = "padding:5px 7px;font-size:9.5px;color:#1F1F1F;border-right:1px solid #D4B5B8;vertical-align:top;line-height:1.35;";
    return `
    <style>
      @page { background: #FBF4F5; }
      body { background: #FBF4F5; }
    </style>
    <div style="background:#FBF4F5;padding:14px;margin:-8px -8px 0;">
    ${logoOnlyHeader("COMMERCIAL INVOICE")}

    <table style="width:100%;border-collapse:collapse;border:1px solid #D4B5B8;margin-bottom:0;">
      <tr>
        <td style="width:33%;${partyHead}">EXPORTER:</td>
        <td style="width:33%;${partyHead}">SHIPPER:</td>
        <td style="width:34%;${partyHead.replace('border-right:1px solid #D4B5B8;', '')}">CONSIGNEE:</td>
      </tr>
      <tr>
        <td style="${partyCell}">${COMPANY.name}<br>${COMPANY.addr}<br>Email: ${COMPANY.email}<br>Name: ${COMPANY.contact}<br>${COMPANY.phone} UAE</td>
        <td style="${partyCell}">${esc(v.shipper_name)}<br>${ml(v.shipper_addr)}<br>${esc(v.shipper_email)}<br>${esc(v.shipper_phone)}</td>
        <td style="${partyCell.replace('border-right:1px solid #D4B5B8;', '')}">${esc(v.consignee_name)}<br>${esc(v.consignee_addr)}<br>${esc(v.consignee_country)}<br>${esc(v.consignee_email)}<br>${esc(v.consignee_phone)}</td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;border-left:1px solid #D4B5B8;border-right:1px solid #D4B5B8;">
      <tr>
        <td style="width:25%;${cellLabel}">Export License No</td>
        <td style="width:25%;${cellLabel}">Commercial Invoice Date</td>
        <td style="width:25%;${cellLabel}">Commercial Invoice Number</td>
        <td style="width:25%;${cellLabelLast}">Container No</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.export_license) || "—"}</td>
        <td style="${cellValue}">${fmtDate(v.invoice_date)}</td>
        <td style="${cellValue}">${esc(v.invoice_no) || "—"}</td>
        <td style="${cellValueLast}">${esc(v.container_no) || "—"}</td>
      </tr>
      <tr>
        <td style="${cellLabel}">Method of Dispatch</td>
        <td style="${cellLabel}">Type of Shipment</td>
        <td style="${cellLabel}">Country of origin of goods</td>
        <td style="${cellLabelLast}">Country of final destination</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.method) || "—"}</td>
        <td style="${cellValue}">${esc(v.shipment_type) || "—"}</td>
        <td style="${cellValue}">${esc(v.country_origin) || "—"}</td>
        <td style="${cellValueLast}">${esc(v.country_dest) || "—"}</td>
      </tr>
      <tr>
        <td style="${cellLabel}">Vessel/Aircraft</td>
        <td style="${cellLabel}">Voyage Number</td>
        <td colspan="2" style="${cellLabelLast}">Terms/method of payment</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.vessel) || "—"}</td>
        <td style="${cellValue}">${esc(v.voyage) || "—"}</td>
        <td colspan="2" style="${cellValueLast}">${esc(v.payment_terms)}</td>
      </tr>
      <tr>
        <td style="${cellLabel}">Port of loading</td>
        <td style="${cellLabel}">ETD</td>
        <td style="${cellLabel}">ETA</td>
        <td style="${cellLabelLast}">&nbsp;</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.port_loading) || "—"}</td>
        <td style="${cellValue}">${fmtDate(v.etd)}</td>
        <td style="${cellValue}">${fmtDate(v.eta)}</td>
        <td style="${cellValueLast}">&nbsp;</td>
      </tr>
      <tr>
        <td style="${cellLabel}">Port of Discharge</td>
        <td style="${cellLabel}">Place of Receipt</td>
        <td style="${cellLabel}">Marine Cover Policy</td>
        <td style="${cellLabelLast}">Letter Of Credit No</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.port_discharge) || "—"}</td>
        <td style="${cellValue}">${esc(v.place_of_receipt) || "—"}</td>
        <td style="${cellValue}">${esc(v.marine_policy) || "N/A"}</td>
        <td style="${cellValueLast}">${esc(v.letter_of_credit) || "N/A"}</td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;border-left:1px solid #D4B5B8;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;">
      <tr>
        <td style="width:48%;${cellLabel}">Description of goods</td>
        <td style="width:13%;${cellLabel}text-align:right;">Net Weight</td>
        <td style="width:13%;${cellLabel}text-align:right;">Gross weight</td>
        <td style="width:13%;${cellLabel}text-align:right;">Unit quantity</td>
        <td style="width:13%;${cellLabelLast}text-align:right;">Amount</td>
      </tr>
      <tr>
        <td style="${cellValue}vertical-align:top;">
          <div style="font-weight:bold;margin-bottom:2px;">${esc(v.line_desc)}</div>
          <div style="font-size:8.5px;color:#444;line-height:1.3;">${ml(v.description_text)}</div>
        </td>
        <td style="${cellValue}text-align:right;vertical-align:top;">${esc(v.net_weight)}</td>
        <td style="${cellValue}text-align:right;vertical-align:top;">${esc(v.gross_weight)}</td>
        <td style="${cellValue}text-align:right;vertical-align:top;">${esc(v.unit_qty)}</td>
        <td style="${cellValueLast}text-align:right;vertical-align:top;">${esc(v.price_per_unit) ? `$ ${esc(v.price_per_unit)}` : ""}</td>
      </tr>
      <tr>
        <td colspan="4" style="${cellValue}text-align:right;font-weight:bold;">Total on this page</td>
        <td style="${cellValueLast}text-align:right;font-weight:bold;">${esc(v.line_amount) ? `$ ${esc(v.line_amount)}` : ""}</td>
      </tr>
      <tr>
        <td colspan="4" style="${cellValue}text-align:right;font-weight:bold;">Consignment total</td>
        <td style="${cellValueLast}text-align:right;font-weight:bold;">${esc(v.line_amount) ? `$ ${esc(v.line_amount)}` : ""}</td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;border-left:1px solid #D4B5B8;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;">
      <tr>
        <td style="width:50%;${cellLabel}">Additional Info:</td>
        <td style="width:25%;${cellLabel}">Total</td>
        <td style="width:25%;${cellLabelLast}">&nbsp;</td>
      </tr>
      <tr>
        <td rowspan="3" style="${cellValue}vertical-align:top;font-size:8.5px;line-height:1.3;">${ml(v.additional_info)}</td>
        <td style="${cellValue}font-weight:bold;">${esc(v.line_amount) ? `$ ${esc(v.line_amount)}` : ""}</td>
        <td style="${cellValueLast}">&nbsp;</td>
      </tr>
      <tr>
        <td style="${cellLabel}">Incoterms ${esc(v.incoterm_year)}</td>
        <td style="${cellLabelLast}">Currency</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.incoterm)}</td>
        <td style="${cellValueLast}">${esc(v.currency)}</td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;border-left:1px solid #D4B5B8;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;">
      <tr>
        <td style="width:50%;${cellLabel}">Account Name:</td>
        <td style="width:50%;${cellLabelLast}">Signatory Company</td>
      </tr>
      <tr>
        <td style="${cellValue}font-weight:bold;">${esc(v.signatory_company || COMPANY.name)}</td>
        <td style="${cellValueLast}">${esc(v.signatory_company || COMPANY.name)}</td>
      </tr>
      <tr>
        <td style="${cellLabel}">Account Number</td>
        <td style="${cellLabelLast}">Name of authorised signatory</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.account_no)}</td>
        <td style="${cellValueLast}">${esc(v.signatory)}</td>
      </tr>
      <tr>
        <td style="${cellLabel}">Bank Name</td>
        <td style="${cellLabelLast}">Signature</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.bank_name)}</td>
        <td rowspan="5" style="${cellValueLast}vertical-align:bottom;height:60px;">________________________</td>
      </tr>
      <tr><td style="${cellLabel}">Branch Name</td></tr>
      <tr><td style="${cellValue}">${esc(v.branch_name)}</td></tr>
      <tr><td style="${cellLabel}">BIC Code</td></tr>
      <tr><td style="${cellValue}">${esc(v.bic)}</td></tr>
      <tr>
        <td style="${cellLabel}">IBAN</td>
        <td style="${cellLabelLast}">&nbsp;</td>
      </tr>
      <tr>
        <td style="${cellValue}">${esc(v.iban)}</td>
        <td style="${cellValueLast}">&nbsp;</td>
      </tr>
    </table>

    <div style="margin-top:8px;">
      <div style="color:#6B0F1A;font-weight:bold;font-size:9.5px;margin-bottom:2px;text-transform:uppercase;letter-spacing:0.3px;">Payment Information</div>
      <div style="font-size:9px;color:#1F1F1F;line-height:1.4;">
        ${esc(v.bank_name)}, ${esc(v.branch_name)}<br>
        ${ml(v.bank_address)}
      </div>
    </div>

    ${documentFooter()}
    </div>`;
  },
};

// =============================================================================
// 4. Packing List
// =============================================================================
const PL = {
  id: "packing_list",
  title: "Packing List",
  subtitle: "Pallet and size breakdown",
  defaults: (ctx) => ({
    list_no: ctx.shipment?.reference || "",
    list_date: today(),
    port_discharge: "",
    buyer_name: ctx.buyer?.company_name || "",
    buyer_addr: ctx.buyer?.address || "",
    buyer_country: ctx.buyer?.country || "",
    buyer_email: ctx.buyer?.email || "",
    buyer_phone: ctx.buyer?.phone || "",
    pallets: [], // [{ id, sizes: { 14, 16, 18, 20, 22, 24 } }]
    notes: "Inspect goods upon receipt.\nGoods received in good condition.",
  }),
  sections: [
    { id: "header", title: "Header", fields: [
      { key: "list_no", label: "Packing List No.", type: "text", span: 1 },
      { key: "list_date", label: "Date", type: "date", span: 1 },
      { key: "port_discharge", label: "Port of Discharge", type: "text", span: 2 },
    ]},
    { id: "buyer", title: "Billed To", fields: [
      { key: "buyer_name", label: "Buyer", type: "text", span: 2 },
      { key: "buyer_addr", label: "Address", type: "text", span: 1 },
      { key: "buyer_country", label: "Country", type: "text", span: 1 },
      { key: "buyer_email", label: "Email", type: "text", span: 1 },
      { key: "buyer_phone", label: "Phone", type: "text", span: 1 },
    ]},
  ],
  // Marks this template as supporting an in-form pallet table editor.
  palletEditor: {
    title: "Pallet-by-Pallet Breakdown",
    helper: "Add one row per pallet. Enter how many boxes of each size are on that pallet. Row total and column totals are computed automatically.",
    sizes: [14, 16, 18, 20, 22, 24],
  },
  advancedSections: [
    { id: "notes", title: "Notes", fields: [{ key: "notes", label: "Footer notes (one per line)", type: "textarea", span: "full", rows: 3 }]},
  ],
  render(v) {
    const sizes = [14, 16, 18, 20, 22, 24];
    const pallets = Array.isArray(v.pallets) ? v.pallets : [];
    const sumSize = (s) => pallets.reduce((acc, p) => acc + (parseInt(p?.sizes?.[s] || 0, 10) || 0), 0);
    const palletTotal = (p) => sizes.reduce((acc, s) => acc + (parseInt(p?.sizes?.[s] || 0, 10) || 0), 0);
    const colTotals = sizes.map(sumSize);
    const grandTotal = colTotals.reduce((a, b) => a + b, 0);
    const cellH = "padding:6px 8px;font-size:9.5px;font-weight:bold;text-transform:uppercase;letter-spacing:0.4px;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;text-align:center;color:#6B0F1A;";
    const cellHLast = cellH.replace("border-right:1px solid #D4B5B8;", "");
    const cellD = "padding:5px 8px;font-size:10.5px;border-right:1px solid #D4B5B8;border-bottom:1px solid #D4B5B8;text-align:center;color:#1F1F1F;";
    const cellDLast = cellD.replace("border-right:1px solid #D4B5B8;", "");
    return `
    <style>
      @page { background: #FBF4F5; }
      body { background: #FBF4F5; }
    </style>
    <div style="background:#FBF4F5;padding:14px;">
    ${logoOnlyHeader("PACKING LIST")}

    <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
      <tr>
        <td style="width:55%;vertical-align:top;font-size:11px;line-height:1.6;">
          <div style="font-weight:bold;font-size:11px;color:#6B0F1A;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">Billed to:</div>
          <div style="color:#1F1F1F;">
            ${esc(v.buyer_name)}<br>
            ${esc(v.buyer_addr)}<br>
            ${esc(v.buyer_country)}<br>
            ${esc(v.buyer_email)}<br>
            ${esc(v.buyer_phone)}
          </div>
        </td>
        <td style="width:45%;vertical-align:top;font-size:10.5px;line-height:1.7;">
          <div><span style="color:#6B0F1A;font-weight:bold;text-transform:uppercase;font-size:9px;letter-spacing:0.4px;">Packing list No.</span> <span style="color:#1F1F1F;font-weight:bold;">${esc(v.list_no)}</span></div>
          <div><span style="color:#6B0F1A;font-weight:bold;text-transform:uppercase;font-size:9px;letter-spacing:0.4px;">Date:</span> <span style="color:#1F1F1F;">${fmtDate(v.list_date)}</span></div>
          <div><span style="color:#6B0F1A;font-weight:bold;text-transform:uppercase;font-size:9px;letter-spacing:0.4px;">Port of Discharge:</span> <span style="color:#1F1F1F;">${esc(v.port_discharge)}</span></div>
        </td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;border:1.5px solid #6B0F1A;border-radius:10px;background:white;font-size:10.5px;">
      <tr style="background:#FBF4F5;">
        <th style="${cellH}width:11%;">No. of<br>Pallate</th>
        ${sizes.map((s) => `<th style="${cellH}width:11%;">Size ${s}</th>`).join("")}
        <th style="${cellHLast}width:12%;">Total</th>
      </tr>
      ${pallets.length === 0
        ? `<tr><td colspan="${sizes.length + 2}" style="${cellDLast}padding:14px;color:#888;">No pallets added — use the editor to add pallet rows.</td></tr>`
        : pallets.map((p, idx) => `
        <tr>
          <td style="${cellD}">${idx + 1}</td>
          ${sizes.map((s) => `<td style="${cellD}">${p?.sizes?.[s] !== "" && p?.sizes?.[s] != null ? esc(p.sizes[s]) : ""}</td>`).join("")}
          <td style="${cellDLast}font-weight:bold;color:#6B0F1A;">${palletTotal(p) || ""}</td>
        </tr>`).join("")}
      <tr style="background:#F4E8E9;">
        <td style="${cellD}font-weight:bold;text-transform:uppercase;letter-spacing:0.4px;color:#6B0F1A;">Total</td>
        ${colTotals.map((t) => `<td style="${cellD}font-weight:bold;color:#6B0F1A;">${t || ""}</td>`).join("")}
        <td style="${cellDLast}font-weight:bold;color:#6B0F1A;">${grandTotal || ""}</td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;margin-top:24px;">
      <tr>
        <td style="width:55%;vertical-align:top;font-size:11px;line-height:1.6;">
          <div style="font-weight:bold;text-transform:uppercase;letter-spacing:0.4px;font-size:11px;margin-bottom:4px;color:#6B0F1A;">NOTES :</div>
          <div style="color:#1F1F1F;">${(v.notes || "").split("\n").filter(Boolean).map((n) => esc(n)).join("<br>")}</div>
        </td>
        <td style="width:45%;vertical-align:bottom;text-align:center;">
          <div style="height:50px;border-bottom:1px solid #6B0F1A;margin-bottom:4px;"></div>
          <div style="font-size:10px;color:#6B0F1A;font-weight:bold;text-transform:uppercase;letter-spacing:0.4px;">Authorised Signature</div>
        </td>
      </tr>
    </table>

    ${documentFooter()}
    </div>`;
  },
};

// =============================================================================
// 5. Internal Pre-Shipment Inspection
// =============================================================================
const IPSI = {
  id: "inspection",
  title: "Internal Pre-Shipment Inspection",
  subtitle: "Quality and loading supervision report",
  defaults: (ctx) => ({
    company: COMPANY.name,
    product: ctx.shipment?.product || "",
    declared_qty: "",
    location: "",
    invoice_ref: ctx.shipment?.reference || "",
    conducted_by: "TradeRepubliq Quality Team",
    inspection_date: today(),
    inspection_time: "14:00 – 18:00 hrs",
    container_temp_before: "", container_temp_after: "", target_temp: "",
    pallets_14: "", boxes_14: "",
    pallets_16: "", boxes_16: "",
    pallets_18: "", boxes_18: "",
    pallets_20: "", boxes_20: "",
    pallets_22: "", boxes_22: "",
    pallets_24: "", boxes_24: "",
    total_pallets: "", total_boxes: "",
    dry_matter: "",
    prepared_by: "", verified_by: "", approved_by: "",
    advance_purpose: "This report is issued by TradeRepubliq Quality Control Team following an internal inspection conducted prior to shipment. The purpose was to confirm product quality, packaging condition, cold room compliance, and container loading standards before dispatch.",
    advance_summary: "During the inspection, our team verified the availability of cartons, stored under cold room conditions. Random sampling was conducted across different pallet stacks and carton layers to confirm overall shipment consistency.",
    advance_observations: "• Cartons were properly packed and arranged in an orderly manner.\n• Cold room storage conditions were maintained prior to stuffing.\n• Cargo labeling and carton integrity were verified as satisfactory.\n• Container internal condition was checked and found suitable for loading.",
    advance_conclusion: "Following the physical inspection and quality assessment, the consignment was confirmed to be in acceptable export condition. The produce met the expected specifications in terms of size grading, firmness, external quality, and overall handling. The shipment was therefore approved for loading and export dispatch.",
    photos: [], // [{ id, dataUrl, caption }]
  }),
  // Marks this template as supporting an in-form photo gallery. The editor renders
  // a custom photo-management UI (add / caption / remove) for templates with this flag.
  photoGallery: {
    title: "Photographs",
    helper: "Attach up to 30 pre-shipment photos. They appear in a 2-column grid in the PDF. iPhone HEIC photos are auto-converted.",
    max: 30,
  },
  sections: [
    { id: "head", title: "Inspection Header", fields: [
      { key: "company", label: "Company / Client", type: "text", span: 2 },
      { key: "product", label: "Product", type: "text", span: 1 },
      { key: "declared_qty", label: "Declared Quantity (cartons)", type: "text", span: 1 },
      { key: "location", label: "Inspection Location", type: "text", span: 1 },
      { key: "invoice_ref", label: "Shipment Invoice", type: "text", span: 1 },
      { key: "conducted_by", label: "Conducted By", type: "text", span: 2 },
      { key: "inspection_date", label: "Inspection Date", type: "date", span: 1 },
      { key: "inspection_time", label: "Inspection Time", type: "text", span: 1 },
    ]},
    { id: "temp", title: "Container Temperatures (°C)", fields: [
      { key: "container_temp_before", label: "Before Loading", type: "text", span: 1 },
      { key: "container_temp_after", label: "After Loading", type: "text", span: 1 },
      { key: "target_temp", label: "Target Temperature", type: "text", span: 2 },
    ]},
    { id: "stuff", title: "Stuffing Summary (Pallets / Boxes per size)", fields:
      [14, 16, 18, 20, 22, 24].flatMap((s) => [
        { key: `pallets_${s}`, label: `Size ${s} — Pallets`, type: "text", span: 1 },
        { key: `boxes_${s}`, label: `Size ${s} — Boxes`, type: "text", span: 1 },
      ]).concat([
        { key: "total_pallets", label: "Total Pallets", type: "text", span: 1 },
        { key: "total_boxes", label: "Total Boxes", type: "text", span: 1 },
      ]),
    },
    { id: "dry", title: "Quality Reading", fields: [
      { key: "dry_matter", label: "Dry Matter (%)", type: "text", span: 2 },
    ]},
    { id: "sign", title: "Signatures", fields: [
      { key: "prepared_by", label: "Prepared By", type: "text", span: 1 },
      { key: "verified_by", label: "Verified By", type: "text", span: 1 },
      { key: "approved_by", label: "Approved By", type: "text", span: 2 },
    ]},
  ],
  advancedSections: [
    { id: "purpose", title: "Report Purpose", fields: [{ key: "advance_purpose", label: "Purpose paragraph", type: "textarea", span: "full", rows: 3 }]},
    { id: "summary", title: "Quality Summary", fields: [{ key: "advance_summary", label: "Inspection summary", type: "textarea", span: "full", rows: 3 }]},
    { id: "obs", title: "General Observations", fields: [{ key: "advance_observations", label: "Observations (one per line)", type: "textarea", span: "full", rows: 4 }]},
    { id: "concl", title: "Conclusion", fields: [{ key: "advance_conclusion", label: "Conclusion paragraph", type: "textarea", span: "full", rows: 3 }]},
  ],
  render(v) {
    const sizes = [14, 16, 18, 20, 22, 24];
    return `
    ${markOnlyHeader(60)}
    <h1 style="text-align:center;color:#6B0F1A;font-size:22px;margin:0 0 12px;">Pre-Shipment Quality Inspection Report</h1>
    <table border="1" cellpadding="6" style="width:100%;border-collapse:collapse;margin-bottom:10px;font-size:11px;">
      <tr><td><strong>Company / Client:</strong> ${esc(v.company)}</td><td><strong>Product:</strong> ${esc(v.product)}</td></tr>
      <tr><td><strong>Declared Quantity:</strong> ${esc(v.declared_qty)} Cartons</td><td><strong>Location:</strong> ${esc(v.location)}</td></tr>
      <tr><td><strong>Shipment Invoice:</strong> ${esc(v.invoice_ref)}</td><td><strong>Conducted By:</strong> ${esc(v.conducted_by)}</td></tr>
      <tr><td colspan="2"><strong>Inspection Date / Time:</strong> ${fmtDate(v.inspection_date)} · ${esc(v.inspection_time)}</td></tr>
    </table>

    ${sectionTitle("Report Purpose")}<p>${ml(v.advance_purpose)}</p>
    ${sectionTitle("Quality Inspection Summary")}<p>${ml(v.advance_summary)}</p>

    ${sectionTitle("Container Loading Supervision")}
    <table border="1" cellpadding="6" style="width:100%;border-collapse:collapse;font-size:11px;">
      <tr style="background:#F4E8E9;"><th>Before Loading</th><th>After Loading</th><th>Target</th></tr>
      <tr><td align="center">${esc(v.container_temp_before)} °C</td><td align="center">${esc(v.container_temp_after)} °C</td><td align="center">${esc(v.target_temp)} °C</td></tr>
    </table>

    ${sectionTitle("Stuffing Summary")}
    <table border="1" cellpadding="6" style="width:100%;border-collapse:collapse;font-size:11px;">
      <tr style="background:#F4E8E9;"><th>Size</th><th>Pallets</th><th>Boxes</th></tr>
      ${sizes.map((s) => `<tr><td>${s}</td><td>${esc(v[`pallets_${s}`])}</td><td>${esc(v[`boxes_${s}`])}</td></tr>`).join("")}
      <tr style="background:#FBFAF9;font-weight:bold;"><td>Total</td><td>${esc(v.total_pallets)}</td><td>${esc(v.total_boxes)}</td></tr>
    </table>

    ${sectionTitle("Quality Observations")}
    <table border="1" cellpadding="6" style="width:100%;border-collapse:collapse;font-size:11px;">
      <tr style="background:#F4E8E9;"><th align="left">Parameter</th><th align="left">Result</th></tr>
      <tr><td>External Damage</td><td>Within tolerance</td></tr>
      <tr><td>Discoloration</td><td>Minimal</td></tr>
      <tr><td>Bruising / Browning</td><td>Low</td></tr>
      <tr><td>Firmness Level</td><td>Good</td></tr>
      <tr><td>General Presentation</td><td>Export standard</td></tr>
      <tr><td>Dry Matter</td><td>Within acceptable range (${esc(v.dry_matter)} %)</td></tr>
    </table>

    ${sectionTitle("General Observations")}
    <ul>${(v.advance_observations || "").split("\n").filter(Boolean).map((n) => `<li>${esc(n.replace(/^•\s*/, ""))}</li>`).join("")}</ul>

    ${(v.photos && v.photos.length) ? `
    ${sectionTitle("Photographs")}
    <table style="width:100%;border-collapse:separate;border-spacing:8px;margin-top:6px;table-layout:fixed;">
      ${(() => {
        const cell = (p) => p ? `
          <td style="width:50%;padding:0;vertical-align:top;">
            <div style="border:1px solid #E7E5E4;border-radius:6px;overflow:hidden;background:#FAFAF9;">
              <div style="width:100%;height:170px;overflow:hidden;background:#F5F5F4;">
                <img src="${p.dataUrl}" style="width:100%;height:170px;object-fit:cover;display:block;" />
              </div>
              <div style="font-size:10px;color:#555;padding:4px 6px;min-height:14px;text-align:center;">${p.caption ? esc(p.caption) : "&nbsp;"}</div>
            </div>
          </td>` : `<td style="width:50%;padding:0;"></td>`;
        const rows = [];
        for (let i = 0; i < v.photos.length; i += 2) {
          rows.push(`<tr>${cell(v.photos[i])}${cell(v.photos[i + 1])}</tr>`);
        }
        return rows.join("");
      })()}
    </table>` : ""}

    ${sectionTitle("Conclusion")}<p>${ml(v.advance_conclusion)}</p>

    <table style="width:100%;margin-top:24px;"><tr>
      <td style="padding:6px;width:33%;"><strong>Prepared By</strong><br><br>${esc(v.prepared_by) || "____________________"}</td>
      <td style="padding:6px;width:33%;"><strong>Verified By</strong><br><br>${esc(v.verified_by) || "____________________"}</td>
      <td style="padding:6px;width:34%;"><strong>Approved By</strong><br><br>${esc(v.approved_by) || "____________________"}</td>
    </tr></table>`;
  },
};

export const DOCUMENT_TEMPLATES = [SLA, SC, CI, PL, IPSI];
